(() => {
  if (customElements.get('selfiefur-shared-sizes')) return;

  window.SelfieFurSharedSizes = {
    withSelection(url, root) {
      const picker = root.querySelector('selfiefur-shared-sizes');
      if (!picker?.selectedSize || !picker.dataset.productId) return url;
      const destination = new URL(url, window.location.href);
      destination.searchParams.set('selfiefur_size', picker.selectedSize);
      destination.searchParams.set('selfiefur_product', picker.dataset.productId);
      return destination.href;
    },
  };

  const MAX_SHARED_SIZE_CHOICES = 50;

  class SharedSizes extends HTMLElement {
    connectedCallback() {
      const params = new URLSearchParams(window.location.search);
      this.selectedSize = params.get('selfiefur_product') === this.dataset.productId
        ? params.get('selfiefur_size') || '' : '';
      this.signature = '';
      this.onClick = this.handleClick.bind(this);
      this.onChange = () => this.scheduleRefresh();
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.observer = new MutationObserver((records) => {
        if (records.some(({ target }) => !target.closest('[data-shared-size-ui]') && !target.matches('[data-shared-size-property]'))) {
          this.scheduleRefresh();
        }
      });
      this.observer.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'class', 'disabled', 'value'],
      });
      this.refresh();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
    }

    scheduleRefresh() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.refresh());
    }

    get choices() {
      return [...this.querySelectorAll('[data-shared-size-native] input[type="radio"]')].flatMap((input) => {
        const parts = input.value.trim().split(/\s*(?:[/–—-]|\ba\b|\bal\b)\s*/i);
        let shared = parts.length > 1
          && parts.length <= MAX_SHARED_SIZE_CHOICES
          && parts.every((size) => /^\d{2,3}$/.test(size))
          && new Set(parts.map(Number)).size === parts.length;
        let sizes = shared ? parts : [input.value];
        if (shared && parts.length === 2) {
          const [start, end] = parts.map(Number);
          const steps = (end - start) / 2;
          shared = Number.isInteger(steps) && steps > 0 && steps < MAX_SHARED_SIZE_CHOICES;
          sizes = shared
            ? Array.from({ length: steps + 1 }, (_, index) => String(start + index * 2))
            : [input.value];
        }
        return sizes.map((size) => ({ input, size, shared }));
      });
    }

    refresh() {
      const native = this.querySelector('[data-shared-size-native]');
      const ui = this.querySelector('[data-shared-size-ui]') || this.createUI();
      const choices = this.choices;
      const enhanced = choices.some((choice) => choice.shared);
      if (!native) return;

      this.toggleAttribute('data-shared-size-active', enhanced);
      native.hidden = enhanced;
      ui.hidden = !enhanced;
      if (!enhanced) {
        this.querySelector('[data-shared-size-property]')?.remove();
        this.selectedSize = '';
        this.signature = '';
        return;
      }

      const selectedChoices = choices.filter(({ input }) => input.checked);
      const selected = selectedChoices.find(({ size }) => size === this.selectedSize) || selectedChoices[0];
      this.selectedSize = selected?.size || '';
      const signature = JSON.stringify(choices.map(({ input, size }) => [input.id, input.value, size, input.className, input.disabled]));
      if (signature !== this.signature || !ui.querySelector('[data-shared-size-choice]')) {
        this.renderChoices(ui, native, choices);
        this.signature = signature;
      }

      ui.querySelectorAll('[data-shared-size-choice]').forEach((button, index) => {
        const choice = choices[index];
        button.setAttribute('aria-pressed', String(choice === selected));
      });

      let property = this.querySelector('[data-shared-size-property]');
      if (!property) {
        property = document.createElement('input');
        property.type = 'hidden';
        property.setAttribute('data-shared-size-property', '');
        this.append(property);
      }
      property.setAttribute('form', this.dataset.formId);
      property.name = `properties[${this.dataset.propertyLabel}]`;
      property.disabled = !selected?.shared;
      property.value = selected?.shared ? selected.size : '';


    }

    createUI() {
      const ui = document.createElement('div');
      ui.setAttribute('data-shared-size-ui', '');
      this.append(ui);
      return ui;
    }

    renderChoices(ui, native, choices) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'radio__fieldset';
      const legend = document.createElement('legend');
      legend.className = 'radio__legend';
      legend.textContent = native.querySelector('.radio__legend__label')?.textContent
        || native.querySelector('legend')?.textContent || '';
      const buttons = document.createElement('div');
      buttons.className = 'selfiefur-shared-sizes__choices';
      const counts = new Map();
      choices.forEach(({ size }) => counts.set(size, (counts.get(size) || 0) + 1));
      choices.forEach(({ input, size }, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.sharedSizeChoice = String(index);
        button.className = 'selfiefur-shared-sizes__choice';
        button.disabled = input.disabled || input.classList.contains('sold-out') || input.classList.contains('unavailable');
        button.textContent = counts.get(size) > 1 ? `${size} (${input.value})` : size;
        buttons.append(button);
      });
      fieldset.append(legend, buttons);
      ui.replaceChildren(fieldset);
    }

    handleClick(event) {
      const button = event.target.closest('[data-shared-size-choice]');
      if (!button || button.closest('selfiefur-shared-sizes') !== this || button.disabled) return;
      const choice = this.choices[Number(button.dataset.sharedSizeChoice)];
      if (!choice) return;
      this.selectedSize = choice.size;
      if (!choice.input.checked) choice.input.click();
      this.refresh();
    }
  }

  customElements.define('selfiefur-shared-sizes', SharedSizes);
})();
